import { useState, useCallback, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { createTask } from 'api/tasks';
import { getAllUsersWithNursesAndAdmins } from 'api/users';

import Input from 'components/input/input';
import Textarea from 'components/textarea/textarea';
import Select from 'components/select/select';
import CustomerSelect from 'components/select/customer_select';
import DateTimePicker from 'components/date_time_picker/date_time_picker';

import { ReactComponent as ArrowRightSVG } from 'icons/arrow-right.svg';

const TaskAdd = () => {
  const [fetching, setFetching] = useState(false);
  const [task, setTask] = useState({});
  const [ownerOptions, setOwnerOptions] = useState([]);
  const [valid, setValid] = useState(true);
  const history = useHistory();

  useEffect(() => {
    (async () => {
      const allOwners = await getAllUsersWithNursesAndAdmins();

      const ownerOptions = allOwners.map((d) => ({
        id: d.id,
        value: d.id,
        label:
          d.role === 'nurse'
            ? `${d.nurse.firstName} ${d.nurse.lastName}`
            : `${d.admin.firstName} ${d.admin.lastName}`,
        email: d.email,
      }));

      setOwnerOptions(ownerOptions);
      setFetching(false);
    })();
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;

    setTask({
      ...task,
      [name]: value,
    });
  };

  const onSelectChange = (field, selectedOption) => {
    setTask({
      ...task,
      [field]: selectedOption,
    });
  };

  const onDateChange = ({ value }) => {
    setTask({
      ...task,
      dueDate: value,
    });
  };

  const checkValidity = useCallback(() => {
    return !!task.title && !!task.customer && !!task.owner && !!task.dueDate;
  }, [task]);

  const onSubmit = useCallback(async () => {
    setFetching(true);

    if (checkValidity()) {
      await createTask({
        ...task,
      });

      history.push('/tasks');
    } else {
      setValid(checkValidity());
      setFetching(false);
    }
  }, [checkValidity, history, task]);

  if (fetching) return <main className="th-main"></main>;

  return (
    <main className="th-main">
      <section className="th-canvas">
        <header className="th-canvas-header">
          <h2 className="th-canvas-title">
            <Link to="/tasks">Tasks</Link>
            <span>
              <ArrowRightSVG />
            </span>
            Add
          </h2>
          <section className="view-actions">
            <button className="btn-green" data-cy="submit" onClick={onSubmit}>
              Submit
            </button>
          </section>
        </header>
        <div>
          <h4 className="midlightgrey f-small fw5 ttu mt15">Details</h4>
          <div className="pa5 pt4 br4 shadow-1 bg-grouped-table mt2 flex flex-column items-start justify-between">
            <div className="w-100 flex flex-column items-start justify-between margin-between">
              <div className="w-100 flex flex-row justify-between margin-between">
                <div className="w-100">
                  <Input
                    name="title"
                    label="Title*"
                    value={task.title}
                    valid={valid || !!task.title}
                    onChange={(event) => onChange(event)}
                  />
                </div>
              </div>
              <div className="w-100 flex flex-row justify-between margin-between">
                <div className="w-33">
                  <CustomerSelect
                    className="customer-select"
                    valid={valid || !!task.customer}
                    label="Customer*"
                    value={task.customer}
                    onChange={(selectedOption) =>
                      onSelectChange('customer', selectedOption)
                    }
                  />
                </div>
                <div className="w-33">
                  <Select
                    className="owner-select"
                    valid={valid || !!task.owner}
                    isMulti={false}
                    label="Owner*"
                    value={task.owner}
                    options={ownerOptions}
                    onChange={(selectedOption) =>
                      onSelectChange('owner', selectedOption)
                    }
                  />
                </div>
                <div className="w-33">
                  <DateTimePicker
                    label="Due date*"
                    valid={valid || !!task.dueDate}
                    showTimeInput={false}
                    value={task.dueDate}
                    onChange={(value) => onDateChange({ value })}
                  />
                </div>
              </div>
              <div className="w-100 flex flex-row justify-between margin-between">
                <Textarea
                  name="note"
                  label="Additional note"
                  value={task.note}
                  valid={true}
                  onChange={(event) => onChange(event)}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default TaskAdd;
